import { useTranslation } from 'react-i18next';
import { BiSolidUpvote } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useGetRoadMapQuery, useSetVoteRoadmapMutation } from '@/app/services/chatbot-rtk.services';
import { BannerHelper } from '@/components/common/BannerToast';
import { PSButton } from '@/components/ui/button';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';

import { WrappedErrorBoundary } from '../WrappedErrorBoundary';

enum Status {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  BETA_TESTING = 'BETA_TESTING',
}

const RoadMapPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const getRoadMap = useGetRoadMapQuery();

  const [VoteRoadmapRequest] = useSetVoteRoadmapMutation();

  const defaultVotes = [
    { id: 'c717b2ca-be72-40af-8f13-52d8569ea393', count: 65 },
    { id: '52b2453d-e8b0-4448-8584-94a3dc85d96c', count: 15 },
    { id: '044a6c55-4843-4a59-aac2-090631a65bcd', count: 79 },
    { id: '48de47c0-9c1a-4ad1-b982-1b7a7b88aad4', count: 95 },
    { id: 'e8b6b5e2-9243-4c73-a860-c9ac9693118a', count: 161 },
    { id: '41e4cee5-fe5c-4d74-b9ea-ae48dc4bd98f', count: 2 },
    { id: '7c542cf4-b926-4efe-acee-ec3024c5d419', count: 105 },
    { id: '9e49bb09-7b38-4b9f-bce6-735528f6fb42', count: 6 },
    { id: '83064e83-7db8-41cf-9bfe-9a62556df75b', count: 17 },
    { id: 'cd6a03de-428d-4694-8637-bd1b687c6902', count: 3 },
    { id: '14058dc7-31ab-4137-a19a-c92f3bc65095', count: 96 },
    { id: '71b8325f-f436-44ab-9430-28f640c3ba77', count: 482 },
    { id: 'b0ddfa81-1428-4819-84b1-a4acda12c74a', count: 2 },
    { id: '95e433b0-9e69-4cce-b06d-2e3318d1cde2', count: 289 },
    { id: '4c7a1b75-19a7-4176-bbd1-83f72ab876e9', count: 274 },
    { id: 'a9277ed1-fb8f-4f64-801b-932d8151a040', count: 192 },
    { id: 'bcefb999-4cd4-4aaa-8464-513cc4c2da2d', count: 138 },
    { id: 'a9a47922-aab3-4645-a308-61a852acbc1f', count: 201 },
    { id: '9570f006-28f4-4e39-9134-be41fd8c7fd0', count: 7 },
  ];

  const calculateVotes = (item: any) => {
    const baseCount = defaultVotes.find(dr => dr.id === item.id)?.count ?? 0;
    const totalVotes = item.up_votes != null ? item.up_votes + baseCount : baseCount;

    return totalVotes > 0 ? totalVotes : '';
  };

  const handleVote = (id: string) => {
    VoteRoadmapRequest(id)
      .unwrap()
      .then(res => {
        toast.success(t('RoadRoadmap/confirmationVote'));
        getRoadMap.refetch();
      })
      .catch(err => {
        toast.error(err?.message);
      });
  };

  const handleContactPageLink = () => {
    const url = `/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel/contact`;
    navigate(url);
  };

  const card = (item: any) => {
    return (
      <div className="tw-border-2 tw-border-gray-200 tw-bg-gray-300 tw-rounded-xl tw-my-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 px-4 py-2 ">
          <div className="tw-font-medium">{item.title}</div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              handleVote(item.id);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleVote(item.id);
              }
            }}
            className="tw-cursor-pointer tw-gap-2 tw-flex tw-items-center tw-border-2 tw-border-gray-200 tw-bg-primary tw-p-1 tw-px-3 tw-rounded-2xl tw-text-white tw-text-sm"
          >
            {calculateVotes(item)}
            <BiSolidUpvote />
          </div>
        </div>
        <div className="  p-4 tw-bg-gray-100 tw-rounded-b-xl tw-break-words">{item.description}</div>
      </div>
    );
  };

  return (
    <section>
      <div className="tw-mx-auto tw-container tw-my-5">
        <BannerHelper
          message={t('HelpBanner/RoadMap/hint')}
          buttons={<PSButton id="btn-road-cta" variant="primary" type="button" onClick={() => handleContactPageLink()} title={t('HelpBanner/RoadMap/cta')} />}
        />
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-my-5 tw-h-full">
          <div className="flex flex-col tw-bg-white tw-p-4 tw-h-full tw-border-4 tw-border-white-100 tw-shadow-md tw-rounded-xl">
            <div className="tw-font-bold tw-w-full tw-flex tw-items-center tw-justify-center tw-bg-gray-200  tw-border tw-rounded-xl py-2">
              {t('RoadRoadmap/t-01')}
            </div>
            {getRoadMap?.data?.tasks
              ?.filter((dr: any) => dr.status === Status.TODO)
              .map((item: any) => (
                <>{card(item)}</>
              ))}
          </div>
          <div className="flex flex-col tw-bg-white tw-p-4 tw-h-full tw-border-4 tw-border-white-100 tw-shadow-md tw-rounded-xl">
            <div className="tw-font-bold tw-w-full tw-flex tw-items-center tw-justify-center tw-bg-gray-200  tw-border tw-rounded-xl py-2">
              {' '}
              {t('RoadRoadmap/t-02')}
            </div>
            {getRoadMap?.data?.tasks
              ?.filter((dr: any) => dr.status === Status.IN_PROGRESS)
              .map((item: any) => (
                <>{card(item)}</>
              ))}
          </div>
          <div className="flex flex-col tw-bg-white tw-p-4 tw-h-full tw-border-4 tw-border-white-100 tw-shadow-md tw-rounded-xl">
            <div className="tw-font-bold tw-w-full tw-flex tw-items-center tw-justify-center tw-bg-gray-200  tw-border tw-rounded-xl py-2">
              {' '}
              {t('RoadRoadmap/t-03')}
            </div>
            {getRoadMap?.data?.tasks
              ?.filter((dr: any) => dr.status === Status.BETA_TESTING)
              .map((item: any) => (
                <>{card(item)}</>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WrappedErrorBoundary(RoadMapPage);
